/**
 * F2 · প্রোফাইল — `/account/profile`
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, frame F2 (360 × 680).
 *
 * Frame note: "The verified number is locked and says why. Account closure is present
 * and honest about the window — hiding it is how apps earn a reputation for trapping
 * people."
 *
 * KNOWN DEFECT 1 (one instance on this frame): the locked number `০১৭১২ ৩৪৫ ৬৭৮` is
 * drawn `font:500 16px/1 Manrope` with `tabular-nums` — Bangla numerals in a family that
 * has none. Set in Anek Bangla at the drawn 16px, leading 1.75 per docs/02's small band.
 * The field is a fixed 54px box with `align-items:center`, so nothing moves.
 *
 * WIRED. `GET /v1/account/profile` supplies every label and every value; `PATCH` to the
 * same path saves the two editable fields and answers with the whole account, so F1's
 * header and this form cannot end up showing two different names.
 *
 * THE PHONE NUMBER IS NOT IN THE PATCH BODY, and the route refuses it if sent. F2 draws
 * it locked with the sentence that says why — "নম্বর বদলাতে যাচাই করতে হবে।" — and
 * changing it goes through the OTP door, not through a profile write.
 *
 * DEVIATION: § 02's `Input` is not used. It is `h-[52px] px-[14px]` with a 6px label
 * gap, and it paints any non-empty value with the 1.5px teal "filled" border; F2 draws
 * 54px fields, 15px padding, a 7px gap and a plain 1px rule on a filled value. The
 * number field also carries a badge inside the box, for which `Input` has no slot.
 * Transcribed as drawn; which geometry is canonical is a design question.
 */
import { useCallback, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button } from '@oprappo/ui';
import type { AccountOverview, ApiErrorBody } from '@oprappo/contracts';
import { Frame, SAND_FRAME, ScreenHeader, SelectChevron } from '../offering/chrome.js';
import { SurfaceStates } from '../../states/boundary.js';
import { useResource } from '../../data/resource.js';
import { errorBodyOf } from '../offering/commerce.js';
import { reportUnmapped } from '../../states/unmapped.js';
import {
  cancelAccountClosure,
  fetchAccount,
  requestAccountClosure,
  saveProfile,
} from './api.js';

/** `500 14px/1.75` ink2 with a 7px gap under it. */
function FieldLabel({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="font-anek font-medium text-[14px] leading-[1.75] text-ink2 mb-[7px]">
      {children}
    </div>
  );
}

/**
 * THE SCREEN IS A PURE FUNCTION OF THE PAYLOAD.
 *
 * The view takes what the route returned and renders it; the container below does the
 * loading. That split is what lets the fidelity tests render this screen from the same
 * `fixtures/api/*.json` file the API's own suite asserts its response against, so the
 * chain the visual gate depends on is checked end to end: drawn frame == fixture == API
 * output == this markup.
 *
 * `null` means "nothing has arrived yet", and the view renders its chrome and no content
 * rather than substituting design data.
 */
/**
 * What the closure block does, passed in rather than called from the view.
 *
 * This file's own header promises the screen is a pure function of the payload — that is
 * what lets the fidelity tests render it from the same fixture the API asserts its
 * response against. Both handlers are optional, so a pixel comparison needs no account.
 */
export interface F2ClosureActions {
  onRequestClose?: (() => void) | undefined;
  onCancelClose?: (() => void) | undefined;
  busy?: boolean | undefined;
}

export function F2ProfileView({
  data,
  closureActions = {},
}: {
  data: AccountOverview | null;
  closureActions?: F2ClosureActions;
}): JSX.Element {
  const navigate = useNavigate();
  const { onRequestClose, onCancelClose } = closureActions;
  const busy = closureActions.busy ?? false;
  // Null until somebody asks, which is almost every account almost always — so the drawn
  // state is the one nearly every render shows, and the second state is the exception.
  const closure = data?.closure ?? null;

  // The two editable fields, held locally only while they are being edited. `null` means
  // "not touched", which is what lets the PATCH send only what changed — and `PATCH`
  // means only what is named is written (`account/sql.ts § coalesce`).
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [emailDraft, setEmailDraft] = useState<string | null>(null);

  const form = data?.form ?? null;
  const profile = data?.profile ?? null;
  const nameValue = nameDraft ?? profile?.name_bn ?? '';
  const emailValue = emailDraft ?? profile?.email ?? '';

  return (
    <Frame className={SAND_FRAME}>
      <ScreenHeader titleBn={form?.title_bn ?? ''} onBack={() => navigate('/account')} />

      <div className="flex-1 p-[16px] oprappo-scroll">
        <div className="flex flex-col gap-[16px]">
          <div>
            <FieldLabel>{form?.name_label_bn ?? ''}</FieldLabel>
            {/*
              * THE INPUT IS INSIDE THE DRAWN BOX, NOT INSTEAD OF IT.
              *
              * F2's own footer is a save button, and a form whose fields cannot be typed
              * into has nothing to save — so the field has to become an `<input>`. But an
              * input carries the UA's own box: `border:2px inset`, `padding:1px 2px`, and
              * a height that follows its font rather than the frame's 54px. Substituting
              * it for the div grew this field and pushed every element below it down; the
              * gate measured the whole screen at 3.02%.
              *
              * So the DIV keeps the geometry — 54px content-box, a 1px rule, 15px
              * padding, `items-center` — and the input sits inside it carrying no box at
              * all. Nothing about the healthy render moves, which is the rule wiring has
              * to obey: `w-full` to fill the row, and border, padding and margin zeroed
              * because Tailwind's preflight is off in this app and the UA's values would
              * otherwise stand.
              */}
            <div className="border border-border bg-white h-[54px] box-content flex items-center px-[15px] font-anek font-normal text-[16px] leading-[1.75] text-ink">
              <input
                value={nameValue}
                onChange={(e) => setNameDraft(e.target.value)}
                aria-label={form?.name_label_bn ?? ''}
                className="w-full bg-transparent border-0 p-0 m-0 font-anek font-normal text-[16px] leading-[1.75] text-ink rounded-none outline-none"
              />
            </div>
          </div>

          <div>
            <FieldLabel>{form?.phone_label_bn ?? ''}</FieldLabel>
            <div className="border border-border bg-sand-deep h-[54px] box-content flex items-center justify-between px-[15px]">
              {/* Bangla numerals — Anek Bangla, not the drawn Manrope. */}
              <div className="font-anek font-medium text-[16px] leading-[1.75] text-body">
                {profile?.phone_bn ?? ''}
              </div>
              <Badge labelBn={form?.phone_badge_bn ?? ''} tone="success" dense className="flex-none" />
            </div>
            <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted mt-[7px]">
              {form?.phone_hint_bn ?? ''}
            </div>
          </div>

          <div>
            <FieldLabel>
              {form?.email_label_bn ?? ''}{' '}
              <span className="text-muted font-normal">{form?.email_optional_bn ?? ''}</span>
            </FieldLabel>
            {/* Same shape as the name field above, and the same reason. */}
            <div className="border border-border bg-white h-[54px] box-content flex items-center px-[15px] font-manrope font-normal text-[16px] leading-[1] text-muted">
              <input
                value={emailValue}
                onChange={(e) => setEmailDraft(e.target.value)}
                aria-label={form?.email_label_bn ?? ''}
                className="w-full bg-transparent border-0 p-0 m-0 font-manrope font-normal text-[16px] leading-[1] text-muted rounded-none outline-none"
              />
            </div>
          </div>

          <div>
            <FieldLabel>{form?.area_label_bn ?? ''}</FieldLabel>
            <div className="border border-border bg-white h-[54px] box-content flex items-center justify-between px-[15px]">
              <div className="font-anek font-normal text-[16px] leading-[1.75] text-ink">
                {profile?.area_bn ?? ''}
              </div>
              <SelectChevron size={17} />
            </div>
          </div>
        </div>

        {/*
          * THE CLOSURE BLOCK, IN THE TWO STATES IT NOW HAS.
          *
          * F2 draws one: the button and the thirty-day note. That is the state before the
          * tap, and until this wave it was the only state there was — the button had no
          * handler, no route and no table behind it, so a promise made in the Privacy
          * Policy, in the Terms of Service and here could be read on three surfaces and
          * invoked on none.
          *
          * The second state is the same geometry with different words: `border-t`, the
          * same `mt-[20px] pt-[16px]`, a 15px line and a 13px line under it at the drawn
          * `mt-[4px]`. Nothing new is drawn, and nothing moves — so the block cannot push
          * the save bar or change the frame's height, which is what a visual diff would
          * catch and what an added panel here would have caused.
          *
          * The colours swap roles, deliberately. Asking is the danger-coloured action and
          * the note is muted; once a request stands, the STATEMENT is the muted one and
          * coming back is the coloured action, because that is the live choice on the
          * screen. `text-link` rather than `text-danger` for the way back: cancelling a
          * closure is not a destructive act.
          */}
        <div className="border-t border-border mt-[20px] pt-[16px]">
          {closure === null ? (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={onRequestClose}
                className="font-anek font-medium text-[15px] leading-[1.75] text-danger bg-transparent border-0 p-0 rounded-none text-left"
              >
                {form?.close_bn ?? ''}
              </button>
              <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted mt-[4px]">
                {form?.close_note_bn ?? ''}
              </div>
            </>
          ) : (
            <>
              <div className="font-anek font-medium text-[15px] leading-[1.75] text-ink">
                {form?.close_pending_bn ?? ''}
              </div>
              <button
                type="button"
                disabled={busy}
                onClick={onCancelClose}
                className="font-anek font-normal text-[13px] leading-[1.75] text-link bg-transparent border-0 p-0 rounded-none text-left mt-[4px]"
              >
                {form?.close_cancel_bn ?? ''}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="bg-white border-t border-border px-[16px] py-[13px]">
        <Button
          onClick={() => {
            // Only what changed. An empty email is a CLEAR — F2 marks the field
            // `— ঐচ্ছিক`, which means removable — and `null` is how the route is told
            // that, because `coalesce` cannot tell an omission from a clear
            // (`account/sql.ts § CLEAR_ACCOUNT_EMAIL`).
            const patch: Parameters<typeof saveProfile>[0] = {};
            if (nameDraft !== null) patch.name_bn = nameDraft;
            if (emailDraft !== null) patch.email = emailDraft.trim() === '' ? null : emailDraft;
            void saveProfile(patch).then(() => {
              setNameDraft(null);
              setEmailDraft(null);
              navigate('/account');
            });
          }}
        >
          {form?.save_bn ?? ''}
        </Button>
      </div>
    </Frame>
  );
}

export function F2Profile(): JSX.Element {
  const load = useCallback((signal: AbortSignal) => fetchAccount(signal), []);
  const { data, error, reload } = useResource(load, 'account');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<ApiErrorBody | null>(null);

  /**
   * One press at a time, and the screen re-reads itself afterwards.
   *
   * Both routes answer with the whole account, so this could set state from the response.
   * It calls `reload()` instead, for the reason every other write on this surface does:
   * two paths into the same state is how F1's header and F2's form came to show different
   * names, and a closure block that disagrees with the server about whether a thirty-day
   * clock is running is the same bug with a legal undertaking attached.
   *
   * A refusal is put where `SurfaceStates` renders it, rather than swallowed into a tap
   * that appears to work. Anything with no designed state is reported through
   * `reportUnmapped`, which is what the transport already does.
   */
  const run = useCallback(
    (call: () => Promise<unknown>): void => {
      if (busy) return;
      setBusy(true);
      setActionError(null);
      call()
        .then(() => {
          reload();
        })
        .catch((cause: unknown) => {
          setActionError(errorBodyOf(cause));
          reportUnmapped('account-closure', cause);
        })
        .finally(() => {
          setBusy(false);
        });
    },
    [busy, reload],
  );

  return (
    <SurfaceStates surface="account" error={actionError ?? error ?? undefined}>
      <F2ProfileView
        data={data}
        closureActions={{
          busy,
          onRequestClose: () => {
            run(requestAccountClosure);
          },
          onCancelClose: () => {
            run(cancelAccountClosure);
          },
        }}
      />
    </SurfaceStates>
  );
}
